import {
  Component,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  HostBinding,
  OnDestroy,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SidePanelConfig {
  /** Panel title shown in the header. */
  title: string;
  /** Optional subtitle shown below the title. */
  subtitle?: string;
  /** Material icon name shown in the header circle. Default: none. */
  icon?: string;
  /** Width of the panel (CSS value). Default: '420px'. */
  width?: string;
  /** Whether to show the close (×) button. Default: true. */
  showCloseButton?: boolean;
  /** Whether to show the minimize (–) button. Default: true. */
  showMinimizeButton?: boolean;
  /** Whether pressing Escape closes the panel. Default: true. */
  closeOnEscape?: boolean;
}

@Component({
  selector: 'app-side-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './side-panel.html',
  styleUrl: './side-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidePanelComponent implements OnDestroy {
  // ── Inputs ────────────────────────────────────────────────────────────────
  isOpen = input<boolean>(false);
  isMinimized = input<boolean>(false);
  config = input<SidePanelConfig>({ title: '' });
  hasUnsavedChanges = input<boolean>(false);

  // ── Outputs ───────────────────────────────────────────────────────────────
  panelClosed = output<void>();
  panelOpened = output<void>();
  panelMinimized = output<void>();
  panelRestored = output<void>();

  // ── Internal state ────────────────────────────────────────────────────────
  readonly showConfirmDialog = signal<boolean>(false);
  readonly headerOffset = signal<string>('0px');

  readonly panelWidth = computed(() => this.config().width ?? '420px');
  readonly title = computed(() => this.config().title);
  readonly subtitle = computed(() => this.config().subtitle ?? '');
  readonly icon = computed(() => this.config().icon ?? '');
  readonly showClose = computed(() => this.config().showCloseButton !== false);
  readonly showMinimize = computed(() => this.config().showMinimizeButton !== false);
  readonly closeOnEscape = computed(() => this.config().closeOnEscape !== false);

  /** Panel is open but not minimized — show the full panel. */
  readonly isExpanded = computed(() => this.isOpen() && !this.isMinimized());
  /** Panel is open and minimized — show the floating tab. */
  readonly isCollapsed = computed(() => this.isOpen() && this.isMinimized());

  @HostBinding('class.side-panel--open')
  get hostOpen(): boolean { return this.isExpanded(); }

  private readonly keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.isExpanded() && this.closeOnEscape()) {
      e.preventDefault();
      this.requestClose();
    }
  };

  constructor() {
    effect(() => {
      const expanded = this.isExpanded();
      const width = this.panelWidth();
      if (expanded) {
        this.measureHeader();
        document.documentElement.style.setProperty('--side-panel-width', width);
        document.body.classList.add('has-side-panel');
        this.panelOpened.emit();
      } else {
        document.documentElement.style.removeProperty('--side-panel-width');
        document.body.classList.remove('has-side-panel');
      }
    });

    // Keep headerOffset up-to-date for the minimized tab too
    effect(() => {
      if (this.isCollapsed()) {
        this.measureHeader();
      }
    });

    document.addEventListener('keydown', this.keyHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.keyHandler);
    document.documentElement.style.removeProperty('--side-panel-width');
    document.body.classList.remove('has-side-panel');
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /** Close button / escape — shows confirm dialog if dirty. */
  requestClose(): void {
    if (this.hasUnsavedChanges()) {
      this.showConfirmDialog.set(true);
    } else {
      this.close();
    }
  }

  confirmClose(): void {
    this.showConfirmDialog.set(false);
    this.close();
  }

  cancelClose(): void {
    this.showConfirmDialog.set(false);
  }

  minimize(): void {
    this.panelMinimized.emit();
  }

  restore(): void {
    this.measureHeader();
    this.panelRestored.emit();
  }

  private close(): void {
    this.panelClosed.emit();
  }

  /** Measure the app header element and set the panel top offset. */
  private measureHeader(): void {
    const header = document.querySelector('header.header') as HTMLElement | null;
    if (header) {
      const rect = header.getBoundingClientRect();
      this.headerOffset.set(`${rect.bottom}px`);
    } else {
      this.headerOffset.set('0px');
    }
  }
}
